<script lang="ts" setup>
import type { Translation } from '@nzyme/i18n-core/Translation.js';
import { assert } from '@nzyme/utils/assert.js';
import { Form } from '@nzyme/vue-forms/components/Form.jsx';
import { defineValidator } from '@nzyme/vue-forms/defineValidator.js';
import { useForm } from '@nzyme/vue-forms/useForm.js';
import { useFormFields } from '@nzyme/vue-forms/useFormFields.js';
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { useEmitAsync } from '@nzyme/vue-utils/useEmitAsync.js';
import { computed } from 'vue';

import type { MeasurementInputValue } from '@sigma/common/forms/MeasurementInput.js';
import type { ScalarMeasurementValue } from '@sigma/common/measurements/ScalarMeasurement.js';
import type { ScalarMeasurementKind } from '@sigma/common/measurements/ScalarMeasurementKind.js';
import { SCALAR_MEASUREMENTS } from '@sigma/common/measurements/scalarMeasurements.js';
import { UnitToken } from '@sigma/common/measurements/UnitToken.js';

import Button from '../../Button.vue';
import ScalarMeasurementInput from '../../forms/ScalarMeasurementInput.vue';
import * as l from './ChatScalarMeasurementInput.loc.js';

interface Props {
  /** The scalar measurement to ask for; selects the {@link SCALAR_MEASUREMENTS} definition. */
  kind: ScalarMeasurementKind;
  /** Validation message shown when no value was entered. */
  errorMessage: Translation;
}

interface Emits {
  submit: [value: MeasurementInputValue];
}

const props = defineProps<Props>();
defineEmits<Emits>();

const t = useTranslate();
const emitAsync = useEmitAsync<Emits>();

const definition = computed(() => SCALAR_MEASUREMENTS[props.kind]);

const measurementValidator = defineValidator<ScalarMeasurementValue | null>({
  async: false,
  validate: value => {
    if (value == null || value.value <= 0) {
      return t(props.errorMessage);
    }
  },
});

const form = useForm({
  measurement: null as ScalarMeasurementValue | null,
});

const fields = useFormFields(form, {
  measurement: [measurementValidator],
});

async function handleSubmit() {
  const valid = await form.validate();
  if (!valid) {
    return;
  }

  assert(form.value.measurement);
  const measurement = form.value.measurement;

  // The form value's unit is the wide string domain; the `UnitToken` enum recovers the narrow
  // union expected by `MeasurementInputValue` without casting (the select only offers those tokens).
  const unit = UnitToken.parse(measurement.unit);

  await emitAsync('submit', {
    type: 'MEASUREMENT',
    kind: props.kind,
    value: measurement.value,
    unit,
  });
}
</script>

<template>
  <Form
    :class="css.formContainer"
    @submit="handleSubmit"
  >
    <ScalarMeasurementInput
      :field="fields.measurement"
      :definition="definition"
      focus
    />

    <Button
      tid="chat-question-submit"
      variant="primary"
      size="large"
      color="black"
      rounded
      :class="css.submitButton"
      type="submit"
    >
      {{ t(l.submitButton) }}
    </Button>
  </Form>
</template>

<style lang="scss" module="css">
.formContainer {
  display: flex;
  flex-direction: column;
  gap: $spacing-08;
}

.submitButton {
  margin-top: $spacing-08;
}
</style>
